import React, { useEffect, useRef } from 'react';
import { ApolloError } from '@apollo/client';
import { onRenderCheckbox } from '@coherence-design-system/controls';
import {
  DetailsListLayoutMode,
  IDetailsRowProps,
  IObjectWithKey,
  IRenderFunction,
  SelectionMode,
  Selection,
  Stack,
  IGroup,
  ConstrainMode,
  IStackStyles,
  IDetailsRowStyles,
  IStyle,
  IColumn,
  CheckboxVisibility,
  IColumnReorderOptions,
  ShimmeredDetailsList,
  IDetailsList,
  NeutralColors,
} from '@fluentui/react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  Toolbar,
  ToolbarButton,
  FluentProvider,
  webLightTheme,
  tokens,
  MessageBar,
  MessageBarBody,
  SearchBox,
} from '@fluentui/react-components';

import {
  CalendarSettingsRegular,
  EditRegular,
  CommentRegular,
  ChatRegular,
} from '@fluentui/react-icons';
import ScreenReaderAnnounce from '../ScreenReaderAnnounce';
import {
  defaultListContainerStyles,
  getSortableColumnHeaderStyles,
  lockedStyledDetailsList,
  resetScrollablePanelStyles,
  styledDetailsListCompact,
} from '../../../app/common/styles/CommonStyleObjects';
import LoadingErrorMessage from '../errorContent/LoadingErrorMessage';
import { IListSortDefinition, SortDirection } from '../../../utils/types/IListSortDefinition';
import { IColumnConfig } from './ColumnConfigHelper';

export interface IToolbarItem {
  key: string;
  text: string;
  iconProps?: { iconName: string };
  disabled?: boolean;
  onClick?: (ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
}

export interface IGeneralEntityListProps<O> {
  data: O[];
  groups?: IGroup[];
  listColumns: IColumnConfig[];
  toolbarItems?: IToolbarItem[];
  onChangeSelectedItems?: (selection: IObjectWithKey[]) => void;
  onEditColumnOrderClick?: () => void;
  onSearchBoxChange?: (event: React.ChangeEvent<HTMLInputElement>, data: { value: string }) => void;
  initialSearchItem?: string;
  onSort?: (col: IColumn) => void;
  sortDefinition?: IListSortDefinition;
  setInitialSelection?: (data: O[], selection: Selection) => void;
  disableSelect?: boolean;
  hideSelectedCount?: boolean;
  loading?: boolean;
  error?: ApolloError;
  layoutMode?: DetailsListLayoutMode;
  lockActionsColumn?: boolean;
  listWrapperStyles?: IStackStyles;
  isHeaderVisible?: boolean;
  setListColumns?: (columns: IColumnConfig[]) => void;
  frozenColumnsFromStart?: number;
  GeneralFilterTagHandler?: () => JSX.Element;
  gridAriaLabel?: string;
}

const RESIZE = 'Resize';
const REORDER = 'Reorder';
const dialogStyles = { main: { maxWidth: '450px' } };

// Helper function to map icon names to Fluent UI v9 icons
const getIconComponent = (iconName?: string) => {
  if (!iconName) return undefined;
  const iconStyle = { color: tokens.colorBrandForeground1 };
  const iconMap: Record<string, JSX.Element> = {
    Edit: <EditRegular style={iconStyle} />,
    Comment: <CommentRegular style={iconStyle} />,
    CannedChat: <ChatRegular style={iconStyle} />,
    CalendarSettings: <CalendarSettingsRegular style={iconStyle} />,
  };
  return iconMap[iconName] || undefined;
};

const GeneralEntityList = <O,>(props: IGeneralEntityListProps<O>): JSX.Element => {
  const {
    data,
    toolbarItems,
    groups,
    onChangeSelectedItems,
    listColumns,
    onEditColumnOrderClick,
    onSearchBoxChange,
    initialSearchItem,
    setInitialSelection,
    disableSelect,
    hideSelectedCount,
    loading,
    error,
    layoutMode = DetailsListLayoutMode.justified,
    lockActionsColumn,
    listWrapperStyles,
    isHeaderVisible,
    onSort,
    sortDefinition,
    setListColumns,
    frozenColumnsFromStart,
    GeneralFilterTagHandler,
    gridAriaLabel,
  } = props;
  const searchBoxWrapperStyle: React.CSSProperties = {
    width: 'auto',
    marginTop: '5px',
    marginLeft: '9px',
    marginBottom: '5px',
  };
  const selectedDividerStyles = {
    paddingLeft: '5px',
    paddingTop: '2px',
    marginBottom: '5px',
    borderLeft: `1px solid ${NeutralColors.gray110}`,
  };

  const selectionRef = useRef(
    new Selection({
      onSelectionChanged: () => {
        if (onChangeSelectedItems) {
          onChangeSelectedItems(selectionRef.current.getSelection());
        }
      },
      items: data as IObjectWithKey[],
    }),
  );

  const sortColumns = () => {
    if (!sortDefinition?.sortDir || !sortDefinition?.sortKey) {
      return listColumns;
    }
    const activeColumns = listColumns.filter((x) => x.active !== false && x.active !== undefined);
    const newColumns: IColumn[] = activeColumns.slice();
    const currentColumn: IColumn = newColumns.find(
      (currCol) => sortDefinition.sortKey === currCol.key,
    );

    newColumns.forEach((newColumn: IColumn) => {
      if (newColumn === currentColumn) {
        newColumn.isSortedDescending = sortDefinition.sortDir === SortDirection.DESC;
        newColumn.isSorted = true;
        newColumn.styles = getSortableColumnHeaderStyles(true);
      } else {
        newColumn.isSorted = false;
        newColumn.isSortedDescending = true;
        newColumn.styles = getSortableColumnHeaderStyles(false);
      }
    });
    return newColumns;
  };
  const sortedColumns = sortColumns();
  // If the data source changes we must unselect
  useEffect(() => {
    if (loading && !hideSelectedCount) {
      selectionRef.current.setAllSelected(false);
    }
    if (!loading && data?.length > 0 && setInitialSelection) {
      setInitialSelection(data, selectionRef.current);
    }
  }, [loading]);

  // Fix to allow user to select text from columns. Without it, row selection
  // code takes over the highlight functionality
  const onRenderRow = (
    rowProps: IDetailsRowProps,
    defaultRender?: IRenderFunction<IDetailsRowProps>,
  ): JSX.Element => {
    const customStyles: Partial<IDetailsRowStyles> = { root: {} as IStyle };
    customStyles.root = { userSelect: 'any' };
    rowProps.styles = customStyles;
    const ariaLabelCheckbox = rowProps?.item?.name;
    if (ariaLabelCheckbox?.length > 0) {
      rowProps.checkButtonAriaLabel = `Select ${rowProps.item.name}`;
    }
    return <>{defaultRender && defaultRender(rowProps)}</>;
  };

  const handleColumnReorder = (draggedIndex: number, targetIndex: number) => {
    const draggedItems = sortedColumns[draggedIndex];
    const newColumns: IColumn[] = [...sortedColumns];

    // insert before the dropped item
    newColumns.splice(draggedIndex, 1);
    newColumns.splice(targetIndex, 0, draggedItems);
    // reset columns
    setListColumns(
      newColumns.map((sc: IObjectWithKey, index: number) => {
        return {
          ...sc,
          key: sc.key,
          active: true,
          order: index,
        } as IColumnConfig;
      }),
    );
  };

  const getColumnReorderOptions = (): IColumnReorderOptions => {
    return {
      frozenColumnCountFromStart: frozenColumnsFromStart || 0,
      frozenColumnCountFromEnd: 0,
      handleColumnReorder,
    };
  };

  // This is used to setup keyboard controls for columns. This can be removed on the update to fluent 9
  const [menuOpenColumn, setMenuOpenColumn] = React.useState<IColumn | null>(null);
  const [menuTargetElement, setMenuTargetElement] = React.useState<HTMLElement | null>(null);
  const [isDialogHidden, setIsDialogHidden] = React.useState(true);
  const columnToEdit = React.useRef<IColumn | null>(null);
  const clickHandler = React.useRef<string>(RESIZE);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const input = React.useRef<number | null>(null);
  const detailsListRef = React.useRef<IDetailsList>(null);

  const resizeColumn = (column: IColumn) => {
    columnToEdit.current = column;
    clickHandler.current = RESIZE;
    setIsDialogHidden(false);
  };

  const reorderColumn = (column: IColumn) => {
    columnToEdit.current = column;
    clickHandler.current = REORDER;
    setIsDialogHidden(false);
  };

  const getMenuItems = (
    column: IColumn,
  ): Array<{ key: string; text: string; onClick: () => void }> | undefined => {
    return [
      { key: 'resize', text: 'Resize', onClick: () => resizeColumn(column) },
      { key: 'reorder', text: 'Reorder', onClick: () => reorderColumn(column) },
      {
        key: 'sort',
        text: 'Sort',
        onClick: () => {
          if (onSort !== undefined) {
            onSort(column);
          }
        },
      },
    ];
  };

  const confirmDialog = () => {
    if (inputRef.current) {
      input.current = Number(inputRef.current.value);
    }

    if (columnToEdit.current && input.current) {
      if (clickHandler.current === RESIZE) {
        const detailsList = detailsListRef.current;
        const width = input.current;
        detailsList.updateColumn(columnToEdit.current, { width });
      } else if (clickHandler.current === REORDER) {
        const previousIndex = listColumns.findIndex((col) => col.key === columnToEdit.current.key);
        handleColumnReorder(previousIndex, input.current);
      }
    }

    input.current = null;
    clickHandler.current = null;
    setIsDialogHidden(true);
  };
  // End keyboard column controls to be removed on update to fluent 9

  return (
    <>
      <Stack>
        <LoadingErrorMessage loading={false} error={error} />
        {!loading && !error && data && data.length === 0 && (
          <MessageBar intent="info" layout="multiline">
            <MessageBarBody>
              There are currently no items in this list. Please add an item and refresh.{' '}
            </MessageBarBody>
          </MessageBar>
        )}
      </Stack>
      <Stack horizontal horizontalAlign="start" verticalAlign="center">
        {(toolbarItems?.length > 0 || onEditColumnOrderClick) && (
          <FluentProvider theme={webLightTheme}>
            <Toolbar aria-label="Task Actions">
              {toolbarItems?.map((item) => (
                <ToolbarButton
                  key={item.key}
                  icon={getIconComponent(item.iconProps?.iconName)}
                  onClick={item.onClick}
                  disabled={item.disabled}
                  appearance="subtle"
                >
                  {item.text}
                </ToolbarButton>
              ))}
              {onEditColumnOrderClick && (
                <ToolbarButton
                  icon={<CalendarSettingsRegular style={{ color: tokens.colorBrandForeground1 }} />}
                  onClick={onEditColumnOrderClick}
                  appearance="subtle"
                  aria-label="Edit Columns"
                >
                  Edit Columns
                </ToolbarButton>
              )}
            </Toolbar>
          </FluentProvider>
        )}
        <div style={selectedDividerStyles}>
          {selectionRef.current?.getSelectedCount()} selected
          <ScreenReaderAnnounce message={`${selectionRef.current?.getSelectedCount()} selected`} />
        </div>
      </Stack>

      {onSearchBoxChange && (
        <div style={searchBoxWrapperStyle}>
          <SearchBox
            defaultValue={initialSearchItem}
            onChange={onSearchBoxChange}
            placeholder="Search"
            aria-label="Search"
          />
        </div>
      )}
      {GeneralFilterTagHandler && GeneralFilterTagHandler()}

      <Stack styles={listWrapperStyles || defaultListContainerStyles}>
        <div style={resetScrollablePanelStyles}>
          <ShimmeredDetailsList
            componentRef={detailsListRef}
            enableShimmer={loading}
            shimmerLines={5}
            detailsListStyles={
              lockActionsColumn ? lockedStyledDetailsList : styledDetailsListCompact
            }
            columns={sortedColumns}
            columnReorderOptions={setListColumns ? getColumnReorderOptions() : undefined}
            layoutMode={layoutMode}
            items={data || []}
            groups={groups}
            selection={selectionRef.current}
            selectionMode={disableSelect ? SelectionMode.none : SelectionMode.multiple}
            checkButtonAriaLabel="checkbox"
            ariaLabelForSelectAllCheckbox="select all checkbox"
            ariaLabelForGrid={gridAriaLabel}
            onRenderRow={onRenderRow}
            selectionPreservedOnEmptyClick
            constrainMode={ConstrainMode.unconstrained}
            isHeaderVisible={isHeaderVisible}
            onRenderDetailsHeader={(headerProps, defaultRender) => {
              const headerPropsOverride = { ...headerProps };
              headerPropsOverride.styles = {
                root: {
                  paddingTop: 0,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  background: tokens.colorNeutralBackground1,
                },
              };
              return defaultRender(headerPropsOverride);
            }}
            onColumnHeaderClick={(ev, col) => {
              if (setListColumns) {
                setMenuOpenColumn(col);
                setMenuTargetElement(ev?.currentTarget as HTMLElement);
              }
            }}
            onRenderCheckbox={onRenderCheckbox}
            checkboxVisibility={CheckboxVisibility.always}
          />
          <Menu
            open={menuOpenColumn !== null}
            onOpenChange={() => {
              setMenuOpenColumn(null);
              setMenuTargetElement(null);
            }}
            positioning={{
              target: menuTargetElement,
            }}
          >
            <MenuPopover>
              <MenuList>
                {getMenuItems(menuOpenColumn)?.map((item) => (
                  <MenuItem key={item.key} onClick={item.onClick}>
                    {item.text}
                  </MenuItem>
                ))}
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </Stack>

      <FluentProvider theme={webLightTheme}>
        <Dialog
          open={!isDialogHidden}
          onOpenChange={() => {
            setIsDialogHidden(true);
          }}
        >
          <DialogSurface style={dialogStyles.main}>
            <DialogBody>
              <DialogTitle>
                {clickHandler.current === RESIZE ? 'Resize Column' : 'Reorder Column'}
              </DialogTitle>
              <DialogContent>
                {clickHandler.current === RESIZE
                  ? 'Enter desired column width pixels:'
                  : 'Enter which column to move this to (use 1-based indexing):'}
                <Input
                  ref={inputRef}
                  type="number"
                  aria-label={
                    clickHandler.current === RESIZE ? 'Enter column width' : 'Enter column index'
                  }
                />
              </DialogContent>

              <DialogActions>
                <Button appearance="primary" onClick={confirmDialog}>
                  {clickHandler.current}
                </Button>
                <Button onClick={() => setIsDialogHidden(true)}>Cancel</Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      </FluentProvider>
    </>
  );
};
export default GeneralEntityList;
